import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Blocks, Shield, FileText, Scale, Globe, Lock, BarChart3, Coins } from 'lucide-react';

const FEATURES = [
  {
    icon: Coins,
    title: 'Asset Tokenization',
    description: 'Convert infrastructure deals and mineral contracts into fractional digital tokens for broader investor access.',
    status: 'Planning'
  },
  {
    icon: Scale,
    title: 'Regulatory Compliance Engine',
    description: 'Automated compliance checks across MEA jurisdictions, including KYC/AML and securities regulations.',
    status: 'Planning'
  },
  {
    icon: Shield,
    title: 'Smart Contract Settlement',
    description: 'Programmable escrow and milestone-based fund release using audited smart contracts.',
    status: 'Research'
  },
  {
    icon: FileText,
    title: 'RWA Documentation Framework',
    description: 'Standardized legal templates for Real World Asset tokenization across different deal structures.',
    status: 'Research'
  },
  {
    icon: Globe,
    title: 'Cross-Border Transfer Protocol',
    description: 'Seamless transfer of tokenized assets across jurisdictions with automatic regulatory compliance.',
    status: 'Concept'
  },
  {
    icon: Lock,
    title: 'Institutional Custody',
    description: 'Enterprise-grade custody solutions with multi-signature authorization and insurance coverage.',
    status: 'Concept'
  }
];

const READINESS_METRICS = [
  { label: 'Legal Framework', value: 35, color: 'bg-amber-500' },
  { label: 'Technology Infrastructure', value: 25, color: 'bg-blue-500' },
  { label: 'Regulatory Mapping', value: 45, color: 'bg-green-500' },
  { label: 'Institutional Partnerships', value: 20, color: 'bg-cyan-500' }
];

const getStatusColor = (status) => {
  switch (status) {
    case 'Planning': return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
    case 'Research': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300';
    case 'Concept': return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    default: return 'bg-gray-100 text-gray-700';
  }
};

export function DigitalAssetPage() {
  return (
    <div className="p-6 space-y-6" data-testid="digital-asset-page">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Blocks size={20} />
          <h1 className="text-3xl font-bold tracking-tight">Digital Asset Infrastructure</h1>
        </div>
        <p className="text-muted-foreground">Tokenization readiness and Real World Asset (RWA) infrastructure for INMX</p>
      </div>

      <Card className="border-amber-300 bg-amber-50/50 dark:bg-amber-950/30 dark:border-amber-800">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <Blocks size={20} className="text-amber-600" />
            <div>
              <p className="text-sm font-medium">Coming Soon</p>
              <p className="text-xs text-muted-foreground">Digital asset infrastructure is under active development. Features shown below represent our planned roadmap.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 size={16} />Platform Readiness
            </CardTitle>
            <CardDescription>Current progress toward digital asset capabilities</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {READINESS_METRICS.map(metric => (
              <div key={metric.label}>
                <div className="flex justify-between text-sm mb-1">
                  <span>{metric.label}</span>
                  <span className="font-mono text-muted-foreground">{metric.value}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full ${metric.color} rounded-full transition-all`} style={{ width: `${metric.value}%` }} />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Globe size={16} />Supported Jurisdictions
            </CardTitle>
            <CardDescription>Regulatory frameworks being mapped</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { region: 'UAE (ADGM/DIFC)', status: 'Active', note: 'Virtual Asset Regulatory Authority framework' },
                { region: 'Saudi Arabia (CMA)', status: 'Research', note: 'Capital Markets Authority sandbox' },
                { region: 'South Africa (FSCA)', status: 'Research', note: 'Financial Sector Conduct Authority guidelines' },
                { region: 'Kenya (CMA)', status: 'Planned', note: 'Capital Markets Authority consultation' },
                { region: 'Nigeria (SEC)', status: 'Planned', note: 'Securities & Exchange Commission rules' },
                { region: 'Bahrain (CBB)', status: 'Planned', note: 'Central Bank crypto-asset module' }
              ].map(j => (
                <div key={j.region} className="flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium">{j.region}</p>
                    <p className="text-xs text-muted-foreground">{j.note}</p>
                  </div>
                  <Badge variant="outline" className="text-[10px]">{j.status}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Separator />

      <div>
        <h2 className="text-lg font-semibold mb-4">Planned Capabilities</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map(feature => (
            <Card key={feature.title} data-testid={`feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-muted rounded-sm">
                    <feature.icon size={18} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-sm">{feature.title}</h3>
                      <Badge className={`text-[9px] ${getStatusColor(feature.status)}`}>{feature.status}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
